import { useEffect, useState } from "react";
import type { LegalDocumentView } from "@keydials/api-client";
import { api } from "../auth/AuthContext";
import { SITE_URL } from "../siteUrl";
import { LegalText } from "./LegalText";

/** What the buy button needs to know: which version was accepted, if both boxes are ticked. */
export interface TermsState {
  /** The version accepted, or null until the terms are loaded and both boxes are ticked. */
  acceptedVersion: string | null;
}

/**
 * "Before you continue" (KEY-618): the facts a buyer should know before paying, the End User Terms and
 * Conditions in full with their version, and the two confirmations the checkout will not start without.
 *
 * The text comes from the API — the same versioned copy the website's legal page is built from — and the
 * version shown here is the version sent, so what was on screen is what is recorded.
 *
 * `reloadKey` changes when the server says the terms moved on under an open page: the new text is fetched
 * and both boxes are cleared, so agreement is given again to the words now shown.
 */
export function TermsAcceptance({
  period,
  cardFreeTrial = false,
  sponsoredBy = null,
  disabled,
  reloadKey = 0,
  onChange,
}: {
  period: "Monthly" | "Annual" | null;
  /** The chosen plan starts with a trial that takes no card (KEY-431), so nothing renews until one is added. */
  cardFreeTrial?: boolean;
  /**
   * Somebody else pays (KEY-651): a relative accepting a family-pack invitation. The facts about renewing,
   * refunds and cards are the buyer's, not theirs, so they are told who pays and what that person can see.
   */
  sponsoredBy?: string | null;
  disabled?: boolean;
  reloadKey?: number;
  onChange: (state: TermsState) => void;
}) {
  const [terms, setTerms] = useState<LegalDocumentView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [adult, setAdult] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAgreed(false);
    setAdult(false);
    api
      .getTerms()
      .then((t) => !cancelled && setTerms(t))
      .catch((e: unknown) => !cancelled && setLoadError(e instanceof Error ? e.message : "Couldn't load the terms."));
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    onChange({ acceptedVersion: terms && agreed && adult ? terms.version : null });
  }, [terms, agreed, adult, onChange]);

  const updated = terms
    ? new Date(`${terms.version}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })
    : "";
  const legal = (slug: string, label: string) => (
    <a href={`${SITE_URL}/${slug}`} target="_blank" rel="noreferrer">
      {label}
    </a>
  );

  return (
    <section className="buy-terms" aria-labelledby="buy-terms-heading">
      <h2 id="buy-terms-heading">Before you continue</h2>

      {sponsoredBy ? (
        <ul className="buy-facts">
          <li>{sponsoredBy} pays for this plan. Nothing is charged to you, and no card is needed.</li>
          <li>
            Your dial is yours alone. {sponsoredBy} can see that you have joined, but not anything you put in it.
          </li>
        </ul>
      ) : (
      <ul className="buy-facts">
        {/* A card-free trial renews nothing (KEY-638): it ends on its last day unless a card is added, and only
            then does the plan run on. Saying "renews until you cancel" about it would be the wrong promise. */}
        <li>
          {cardFreeTrial
            ? `No card is taken now and nothing is charged. Your free trial simply ends on its last day unless you add a card under Your settings — from then your plan renews ${
                period === "Annual" ? "every year" : period === "Monthly" ? "every month" : "automatically"
              } until you cancel.`
            : period === "Annual"
              ? "Your plan renews every year until you cancel."
              : period === "Monthly"
                ? "Your plan renews every month until you cancel."
                : "Your plan renews automatically until you cancel."}{" "}
          You can cancel from your account at any time.
        </li>
        <li>
          Changed your mind? You have 7 days to cancel for a full refund — see our {legal("refund-policy", "Refund Policy")}.
        </li>
        <li>Payment is taken securely by Stripe. Your card details never reach Key Dials.</li>
      </ul>
      )}

      {loadError ? (
        <p className="buy-error">{loadError}</p>
      ) : !terms ? (
        <p className="buy-muted">Loading the terms…</p>
      ) : (
        <>
          <div className="buy-terms-box" tabIndex={0} role="region" aria-label={terms.title}>
            <h3 className="buy-terms-title">{terms.title}</h3>
            <p className="buy-terms-version">Last updated: {updated}</p>
            <LegalText blocks={terms.blocks} siteUrl={SITE_URL} />
          </div>

          <label className="buy-check">
            <input type="checkbox" checked={agreed} disabled={disabled} onChange={(e) => setAgreed(e.target.checked)} />
            <span>
              I have read and agree to the {legal(terms.slug, terms.title)}, and I have read the{" "}
              {legal("privacy-policy", "Privacy Policy")}.
            </span>
          </label>
          <label className="buy-check">
            <input type="checkbox" checked={adult} disabled={disabled} onChange={(e) => setAdult(e.target.checked)} />
            <span>I confirm I am 18 or over.</span>
          </label>
        </>
      )}
    </section>
  );
}
